import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';

import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IconFieldModule } from 'primeng/iconfield';
import { SelectModule } from 'primeng/select';
import { DrawerModule } from 'primeng/drawer';

import { ClientService } from '@/services/client.service';
import { ShortcutService } from '@/services/shortcut.service';
import { DocumentService } from '@/services/document.service';
import { LoginService } from '@/services/login.service';
import { Tooltip } from 'primeng/tooltip';
import { ProcessService } from '@/services/process.service';

@Component({
    selector: 'app-shortcut',
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, ButtonModule, DialogModule, IconFieldModule, SelectModule, DrawerModule, Tooltip],
    templateUrl: './shortcut.component.html',
    styleUrl: './shortcut.component.scss'
})
export class ShortcutComponent implements OnInit, OnDestroy {
    isShowDialog = false;
    isSuperUser = false;
    isHovering = false;
    iconPosition: number = 0;

    shortcutfrom!: FormGroup;

    clientOptions: any[] = [];
    applicationOptions: any[] = [];
    customerOptions: any[] = [];
    documentOptions: any[] = [];
    processOptions: any[] = [];
    currentRenderedPage = ''; // default page
    private isDragging = false;
    private startY = 0;
    private startTop = 0;
    private hasDragged = false;
    private destroy$ = new Subject<void>();

    documentTypeMap: Record<number, string> = {
        5: 'Invoice',
        10: 'Packing List',
        15: 'Bill of Lading',
        20: 'Death Certificate',
        25: 'Transport Instruction',
        79: 'Transmittal Letter'
    };

    constructor(
        private fb: FormBuilder,
        private router: Router,
        private clientService: ClientService,
        private shortcutService: ShortcutService,
        private documentService: DocumentService,
        private loginService: LoginService,
        private route: ActivatedRoute,
        private processService: ProcessService
    ) { }

    ngOnInit(): void {
        this.currentRenderedPage = this.loginService.getRenderedPage() || '';
        this.buildForm();
        this.initializeByRole();
        this.restoreIconPosition();
        this.clientService.shortcutReset$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.resetShortcutForm();
        });

        // this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
        //     this.syncDrawerWithUrl();
        // });
        // this.syncDrawerWithUrl();
    }

    private buildForm(): void {
        let clientId = this.loginService.getClientId();
        let applicationId = this.loginService.getApplicationId();
        let customerId = this.loginService.getCustomerId();
        let documentId = this.loginService.getDocumentId();
        let processId = this.loginService.getProcessId();
        this.shortcutfrom = this.fb.group({
            client_id: [clientId, Validators.required],
            application_id: [applicationId],
            customer_id: [customerId],
            document_id: [documentId],
            process_type_id: [processId]
        });

        const storedApplicationOptions = localStorage.getItem('applicationOptions');
        const storedCustomerOptions = localStorage.getItem('customerOptions');
        const storedDocumentOptions = localStorage.getItem('documentOptions');
        const storedProcessOptions = localStorage.getItem('processOptions')
        if (clientId) {
            if (storedApplicationOptions) {
                this.applicationOptions = JSON.parse(storedApplicationOptions);
            } else {
                this.loadApplications(clientId);
            }
        }
        if (clientId && applicationId) {
            if (storedProcessOptions) {
                this.processOptions = JSON.parse(storedProcessOptions);
            } else {
                this.loadProcesses(clientId, applicationId);
            }
        }
        if (clientId && applicationId && processId) {
            if (storedCustomerOptions) {
                this.customerOptions = JSON.parse(storedCustomerOptions);
            } else {
                this.loadCustomers(clientId, applicationId, processId);
            }
        }
        if (clientId && applicationId && customerId && processId) {
            if (storedDocumentOptions) {
                this.documentOptions = JSON.parse(storedDocumentOptions);
            } else {
                this.loadDocuments(clientId, applicationId, customerId, processId);
            }
            // if (this.currentRenderedPage === 'Documents') {
            //     this.loadProcesses(clientId, applicationId, customerId);
            // }
        }
    }

    private initializeByRole(): void {
        this.isSuperUser = this.loginService.isSuperUser();

        if (this.isSuperUser) {
            this.shortcutfrom.get('client_id')?.setValidators(Validators.required);
            this.shortcutfrom.get('client_id')?.updateValueAndValidity();
            const storedClientOptions = localStorage.getItem('clientOptions');
            if (!storedClientOptions) {
                this.loadClients();
            }
            else {
                this.clientOptions = JSON.parse(storedClientOptions);
            }
        } else {
            const clientId = this.loginService.getClientId();
            this.shortcutfrom.patchValue({ client_id: clientId });
            const storedApplicationOptions = localStorage.getItem('applicationOptions');
            if (storedApplicationOptions) {
                this.applicationOptions = JSON.parse(storedApplicationOptions);
            } else {
                this.loadApplications(clientId);
            }
        }
    }

    private restoreIconPosition(): void {
        const storedPosition = localStorage.getItem('drawerIconPosition');
        if (storedPosition) {
            this.iconPosition = parseInt(storedPosition, 10);
        } else {
            // Default position: center of screen
            this.iconPosition = window.innerHeight / 2 - 24; // 24 is approximate icon height
        }
    }

    // private syncDrawerWithUrl(): void {
    //     let route = this.route.root;
    //     while (route.firstChild) {
    //         route = route.firstChild;
    //     }

    //     const params = route.snapshot.paramMap;

    //     if (params.has('client_id') && params.has('application_id') && params.has('customer_id')) {
    //         const clientId = Number(params.get('client_id'));
    //         const applicationId = Number(params.get('application_id'));
    //         const customerId = Number(params.get('customer_id'));

    //         if (this.isSuperUser) {
    //             this.shortcutfrom.patchValue({ client_id: clientId }, { emitEvent: false });
    //             this.loadApplications(clientId);
    //         }

    //         this.shortcutfrom.patchValue(
    //             {
    //                 application_id: applicationId,
    //                 customer_id: customerId
    //             },
    //             { emitEvent: false }
    //         );

    //         this.loadCustomers(clientId, applicationId);
    //         this.loadDocuments(clientId, applicationId, customerId);
    //         if (this.currentRenderedPage === 'Documents') {
    //             this.loadProcesses(clientId, applicationId, customerId);
    //         }
    //     }
    // }

    onOpenDialog(): void {
        this.isShowDialog = true;
    }

    private loadClients(): void {
        this.shortcutService.getApplicationShortcut({ is_active: true }).subscribe((res: any) => {
            const data = res?.data ?? res?.results ?? [];
            this.clientOptions =
                data?.map((a: any) => ({
                    label: a.name,
                    value: a.id
                })) || [];
            localStorage.setItem('clientOptions', JSON.stringify(this.clientOptions));
        });
    }

    private loadApplications(clientId: number): void {
        this.shortcutService.getApplicationShortcut({ client_id: clientId, is_active: true }).subscribe((res: any) => {
            const data = res?.data ?? res?.results ?? [];
            this.applicationOptions =
                data?.map((a: any) => ({
                    label: a.name,
                    value: a.id
                })) || [];
            localStorage.setItem('applicationOptions', JSON.stringify(this.applicationOptions));
        });
    }

    private loadCustomers(clientId: number, applicationId: number, processId: number): void {
        this.shortcutService.getApplicationShortcut({ client_id: clientId, application_id: applicationId, process_type_id: processId, is_active: true }).subscribe((res: any) => {
            const data = res?.data ?? res?.results ?? [];
            this.customerOptions =
                data?.map((c: any) => ({
                    label: c.name,
                    value: c.id
                })) || [];
            localStorage.setItem('customerOptions', JSON.stringify(this.customerOptions));
        });
    }

    private loadDocuments(clientId: number, applicationId: number, customerId: number, processId: number): void {
        this.shortcutService
            .getApplicationShortcut({
                client_id: clientId,
                application_id: applicationId,
                customer_id: customerId,
                process_type_id: processId,
                is_active: true,
                user_id: this.loginService.getUserId()
            })
            .subscribe((res: any) => {
                const documentTypeIds: number[] = res?.data ?? [];

                this.documentOptions =
                    documentTypeIds?.map((typeId: number) => ({
                        label: this.documentTypeMap[typeId] ?? `Unknown (${typeId})`,
                        value: typeId,
                        type: typeId
                    })) || [];
                localStorage.setItem('documentOptions', JSON.stringify(this.documentOptions));
            });
    }

    private loadProcesses(clientId: number, applicationId: number): void {
        this.shortcutService
            .getApplicationShortcut({
                client_id: clientId,
                application_id: applicationId,
                user_id: this.loginService.getUserId(),
                is_active: true,
            })
            .subscribe((res: any) => {
                const data = res?.data ?? res?.results ?? [];

                this.processOptions = data.map((process: any) => ({
                    label: process.name,
                    value: process.id
                }));

                localStorage.setItem(
                    'processOptions',
                    JSON.stringify(this.processOptions)
                );
            });
    }

    onClientChange(event: any): void {
        const clientId = event.value;
        this.shortcutfrom.patchValue({ application_id: null, customer_id: null, document_id: null });
        this.applicationOptions = [];
        this.customerOptions = [];
        this.documentOptions = [];
        if (clientId) {
            this.loadApplications(clientId);
        }
    }

    onApplicationChange(event: any): void {
        const applicationId = event.value;
        const clientId = this.shortcutfrom.get('client_id')?.value;
        this.customerOptions = [];
        this.documentOptions = [];
        this.processOptions = [];
        this.shortcutfrom.patchValue({ customer_id: null, document_id: null, process_type_id: null });
        if (clientId && applicationId) {
            this.loadProcesses(clientId, applicationId);
        }
    }
    onProcessChange(event: any) {
        const applicationId = this.shortcutfrom.get('application_id')?.value;
        const clientId = this.shortcutfrom.get('client_id')?.value;
        const processId = event.value;
        this.customerOptions = [];
        // this.documentOptions = [];
        // this.processOptions = [];
        this.shortcutfrom.patchValue({ customer_id: null, document_id: null});
        if (clientId && applicationId && processId) {
            this.loadCustomers(clientId, applicationId, processId);
        }
    }

    onCustomerChange(event: any): void {
        const customerId = event.value;
        const  client_id = this.shortcutfrom.get('client_id')?.value;
        const  application_id = this.shortcutfrom.get('application_id')?.value;
        const  process_type_id = this.shortcutfrom.get('process_type_id')?.value;
        this.documentOptions = [];
        this.shortcutfrom.patchValue({ document_id: null });
        if (client_id && application_id && customerId && process_type_id) {
            this.loadDocuments(client_id, application_id, customerId, process_type_id);
            if (this.currentRenderedPage === 'Documents') {
                this.loadDocuments(client_id, application_id, customerId, process_type_id);
            }
        }
    }

    onReset(): void {
        this.resetShortcutForm();
        this.loginService.resetSelectedIds();
    }

    onSubmit(): void {

    }

    navigateToDocumentGrid(): void {
        if (!this.shortcutfrom.valid) return;

        const { client_id, application_id, customer_id, document_id, process_type_id } = this.shortcutfrom.value;
        localStorage.setItem('selectedClientId', client_id);
        localStorage.setItem('selectedApplicationId', application_id);
        localStorage.setItem('selectedCustomerId', customer_id);
        localStorage.setItem('selectedDocumentId', document_id);
        localStorage.setItem('selectedProcessId', process_type_id);
        localStorage.setItem('selectedDocumentName', this.documentOptions.find((doc) => doc.value === document_id)?.label || '');
        setTimeout(() => {
            window.location.reload();
        });
    }

    onMouseDown(event: MouseEvent): void {
        event.preventDefault();

        const target = event.currentTarget as HTMLElement;
        this.isDragging = true;
        this.hasDragged = false;

        this.startY = event.clientY;
        this.startTop = this.iconPosition;

        document.addEventListener('mousemove', this.onMouseMove);
        document.addEventListener('mouseup', this.onMouseUp);
    }
    onMouseMove = (event: MouseEvent): void => {
        if (!this.isDragging) return;

        const target = document.querySelector('.drawer-icon') as HTMLElement;

        const deltaY = event.clientY - this.startY;

        if (Math.abs(deltaY) > 5) {
            this.hasDragged = true;
        }

        let newTop = this.startTop + deltaY;

        const minTop = 20;
        const maxTop = window.innerHeight - (target?.offsetHeight || 50) - 20;

        newTop = Math.min(Math.max(newTop, minTop), maxTop);

        this.iconPosition = newTop;
        if (target) {
            target.style.top = `${newTop}px`;
        }
    };

    onMouseUp = (): void => {
        this.isDragging = false;

        // Save the new position to localStorage
        if (this.hasDragged) {
            localStorage.setItem('drawerIconPosition', this.iconPosition.toString());
        }

        document.removeEventListener('mousemove', this.onMouseMove);
        document.removeEventListener('mouseup', this.onMouseUp);
    };
    onIconClick(): void {
        if (this.hasDragged) {
            this.hasDragged = false;
            return;
        }

        this.onOpenDialog();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private resetShortcutForm(): void {
        this.shortcutfrom.reset();
        this.applicationOptions = [];
        this.customerOptions = [];
        this.documentOptions = [];
        this.processOptions = [];

        localStorage.removeItem('applicationOptions');
        localStorage.removeItem('customerOptions');
        localStorage.removeItem('documentOptions');
        localStorage.removeItem('processOptions');

        if (!this.isSuperUser) {
            const clientId = this.loginService.getClientId();
            this.shortcutfrom.patchValue({ client_id: clientId });
            if (clientId) {
                this.loadApplications(clientId);
            }
        }
    }
}
